import { useStore } from '../store/useStore';

export interface ValidationResult {
  valid: boolean;
  type: 'error' | 'warning' | 'success'; 
  message: string;
}

export function useValidators() {
  const shifts = useStore((state) => state.shifts);
  const team = useStore((state) => state.team);
  const points = useStore((state) => state.points);
  const minRestTimeHoursStore = useStore((state) => state.globalSettings.minRestTimeHours);

  // Helper: check if two time intervals overlap
  const doRangesOverlap = (start1: Date, end1: Date, start2: Date, end2: Date) => {
    return start1.getTime() < end2.getTime() && start2.getTime() < end1.getTime();
  };

  const formatDateTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')} à ${d.getHours().toString().padStart(2, '0')}h${d.getMinutes().toString().padStart(2, '0')}`;
  };

  const validateAssignment = (
    teamId: string,
    pointId: string,
    startStr: string,
    endStr: string,
    ignoreShiftId?: string,
    isGeneralPoint?: boolean
  ): ValidationResult[] => {
    const results: ValidationResult[] = [];
    const newStart = new Date(startStr);
    const newEnd = new Date(endStr);

    if (newEnd.getTime() <= newStart.getTime()) {
      results.push({ valid: false, type: 'error', message: "End time must be after start time." });
      return results; // Cannot proceed
    }

    const selectedTeam = team.find(s => s.id === teamId);
    
    // 0. Unavailable Dates
    if (selectedTeam && selectedTeam.unavailableDates) {
      const shiftDateStr = startStr.split('T')[0];
      if (selectedTeam.unavailableDates.includes(shiftDateStr)) {
         results.push({ valid: false, type: 'warning', message: "Indisponibilité : Le team a déclaré ne pas être disponible à cette date." });
      }
    }

    // 1. Alerte Croisement (Double-booking)
    const teamShifts = shifts.filter(s => s.teamId === teamId && s.id !== ignoreShiftId);
    
    let isDoubleBooked = false;
    let conflictingShiftMessage = "";
    
    for (const s of teamShifts) {
      if (doRangesOverlap(newStart, newEnd, new Date(s.start), new Date(s.end))) {
        isDoubleBooked = true;
        const pt = points.find(p => p.id === s.pointId);
        const ptName = pt ? pt.name : "Un autre point";
        conflictingShiftMessage = `Croisement : Le team est déjà assigné sur ${ptName} du ${formatDateTime(s.start)} au ${formatDateTime(s.end)}.`;
        break;
      }
    }
    
    if (isDoubleBooked) {
      results.push({ valid: false, type: 'warning', message: conflictingShiftMessage });
    }

    if (!isGeneralPoint) {
      // 2. Alerte de Repos (Rest Time < 8h) and commute margin
      let minRestTimeMinutes = Infinity;
      let minCommuteTimeMinutes = Infinity;
      
      for (const s of teamShifts) {
        const sStart = new Date(s.start).getTime();
        const sEnd = new Date(s.end).getTime();
        const nStart = newStart.getTime();
        const nEnd = newEnd.getTime();
        
        // Calculate rest time between new shift and existing shift
        if (sEnd <= nStart) {
          // existing shift is BEFORE new shift
          const diff = Math.floor((nStart - sEnd) / 60000); // in minutes
          minRestTimeMinutes = Math.min(minRestTimeMinutes, diff);
          if (s.pointId !== pointId) minCommuteTimeMinutes = Math.min(minCommuteTimeMinutes, diff);
        } else if (sStart >= nEnd) {
          // existing shift is AFTER new shift
          const diff = Math.floor((sStart - nEnd) / 60000); // in minutes
          minRestTimeMinutes = Math.min(minRestTimeMinutes, diff);
          if (s.pointId !== pointId) minCommuteTimeMinutes = Math.min(minCommuteTimeMinutes, diff);
        }
      }

      if (minCommuteTimeMinutes < 30) {
        results.push({ valid: false, type: 'warning', message: `Marge de changement : Prévoir 30 minutes de marge entre deux postes différents (actuellement ${minCommuteTimeMinutes}m).` });
      } else if (minRestTimeMinutes < minRestTimeHoursStore * 60) {
        results.push({ valid: false, type: 'warning', message: `Repos insuffisant : ${Math.floor(minRestTimeMinutes / 60)}h${minRestTimeMinutes % 60}m de repos détectés (min ${minRestTimeHoursStore}h).` });
      }

      // 3. Alerte de Durée (> 7h)
      const durationMinutes = Math.floor((newEnd.getTime() - newStart.getTime()) / 60000);
      if (durationMinutes > 7 * 60) {
        results.push({ valid: false, type: 'warning', message: `Durée excessive : Le shift dure plus de 7 heures (${Math.floor(durationMinutes / 60)}h${durationMinutes % 60}m).` });
      }

      // 4. Alerte Spécialité
      const selectedTeam = team.find(s => s.id === teamId);
      const selectedPoint = points.find(p => p.id === pointId);
      
      if (selectedTeam && selectedPoint) {
        const pointName = selectedPoint.name.toLowerCase();
        const roles = (selectedTeam.roles || [selectedTeam.role]).map(r => r?.toLowerCase() || '');
        
        const isGlobal = roles.includes('global');
        if (!isGlobal && roles.length > 0) {
          let isAuthorized = false;
          
          if (roles.includes('parking only') && pointName.includes('parking')) isAuthorized = true;
          if (roles.includes('cashless only') && pointName.includes('cashless')) isAuthorized = true;
          if (roles.includes('check-in only') && pointName.includes('check-in')) isAuthorized = true;
          
          if (!isAuthorized) {
             results.push({ valid: false, type: 'warning', message: "Spécialité : Ce team ne possède pas l'autorisation pour ce poste." });
          }
        }
      }
    }

    if (results.length === 0) {
       results.push({ valid: true, type: 'success', message: "Validé" });
    }

    return results;
  };

  // Check the filling status of a specific operational point during a specific time slot (e.g. an hour block)
  const getPointStatus = (pointId: string, cellStart: string, cellEnd: string) => {
    const point = points.find(p => p.id === pointId);
    if (!point) return 'error';

    // Count how many team are assigned to THIS point during THIS cell
    // Let's say a cell is just an hour chunk
    const cellStartDt = new Date(cellStart).getTime();
    const cellEndDt = new Date(cellEnd).getTime();

    const overlappingShifts = shifts.filter(s => {
      if (s.pointId !== pointId) return false;
      const sStart = new Date(s.start).getTime();
      const sEnd = new Date(s.end).getTime();
      return cellStartDt < sEnd && sStart < cellEndDt;
    });

    const teamCount = overlappingShifts.length;
    if (teamCount >= point.requiredTeam) return 'success'; // Green
    if (teamCount > 0) return 'warning'; // Orange/Yellow
    return 'danger'; // Red (0/N)
  };

  return {
    validateAssignment,
    getPointStatus,
    doRangesOverlap
  };
}

